import React, { useEffect, useRef, useState, useCallback } from "react";

import { Form, Formik, Field } from "formik";
import { useHistory, useLocation, useParams } from "react-router";
import * as Yup from "yup";
import Swal from "sweetalert2";

import { registerLocale, setDefaultLocale } from "react-datepicker";
import { isBefore, parse, isValid, startOfDay, isEqual } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";
import pt from "date-fns/locale/pt-BR";

import {
  Flex,
  Text,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Grid,
  GridItem,
  Divider,
  Box,
} from "@chakra-ui/react";

import LoadingSpinner from "../../../../components/LoadingSpinner";
import CustomDropzone from "../../../../components/CustomDropzone";

import ServicoAcolhimentoService from "../../../../../main/services/ServicoAcolhimentoService";
import RegularidadeSAService from "../../../../../main/services/RegularidadeSAService";
import { CustomDatepickerWField } from "../../../../components/CustomDatepickerWField";
import { hasFormChanged } from "../../../../utils/hasFormChanged";
import SemRegistros from "../../../../components/SemRegistros";
import { criancaFunc, servicos } from "../../../../../helpers/Funcionalidades";
import { useAppContext } from "../../../../../Context/MessageContextProvider";
import { useAuthProvider } from "../../../../../Context/AuthProvider";
import { scrollToFieldAnexo } from "../../../../utils/scrollToFIeldAnexo";

const CadastrarRegularidade = () => {
  const { id } = useParams();
  const history = useHistory();
  const { roleKeycloack, user } = useAuthProvider();
  const { setCadastrarRegularidadeServ } = useAppContext();
  const suportedFormats = [
    ".png",
    ".bmp",
    ".tiff",
    ".jpg",
    ".jpeg",
    ".doc",
    ".docx",
    ".txt",
    ".dot",
    ".pdf",
    ".xml",
    ".xls",
    ".xlam",
    ".ods",
  ];

  const [data, setData] = useState();
  const [loading, setLoading] = useState(true);
  const [initialValues] = useState({
    numeroRegistro: "",
    validadeNrRegistro: null,
    anexoNrRegistro: null,
    dataAnexoNrRegistro: "",
    numeroInscricao: null,
    validadeNrInscricao: null,
    anexoNrInscricao: null,
    dataAnexoNrInscricao: null,
    numeroInscricaoCMAS: null,
    validadeNrInscricaoCMAS: null,
    anexoNrInscricaoCMAS: null,
    dataAnexoNrInscricaoCMAS: null,
  });
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const idTipoPendencia = searchParams.get("idTipoPendencia");

  const anexoRef = useRef(null);

  const scrollToField = useCallback(
    (ref) => {
      scrollToFieldAnexo(idTipoPendencia, ref);
    },
    [idTipoPendencia],
  );

  useEffect(() => {
    if (["59"].includes(String(idTipoPendencia))) {
      const timeout = setTimeout(() => {
        requestAnimationFrame(() => {
          if (anexoRef.current) {
            scrollToField(anexoRef);
          }
        });
      }, 300);

      return () => clearTimeout(timeout);
    }
  }, [idTipoPendencia, scrollToField]);

  useEffect(() => {
    setDefaultLocale("pt-br");
    registerLocale("pt-br", pt);

    ServicoAcolhimentoService.getServicoAcolhimento(id)
      .then((res) => {
        setData(res.data);
      })
      .catch((err) => {
        Swal.fire({
          title: "Atenção!",
          text: "Serviço de Acolhimento não encontrado",
          icon: "warning",
          confirmButtonText: "Fechar",
          confirmButtonColor: "#169499",
        }).then(() => {
          history.push("/servico-acolhimento");
        });
      });

    RegularidadeSAService.getRegularidadeSA(id)
      .then((res) => {
        history.push(`/servico-acolhimento/visualizar-regularidade/${id}`);
      })
      .catch((erro) => {
        if (erro.response) {
          if (erro.response.data.status !== 404) {
            Swal.fire({
              title: "Atenção!",
              text: "Já existe uma Regularidade cadastrada para este Serviço de Acolhimento!",
              icon: "warning",
              confirmButtonText: "Fechar",
              confirmButtonColor: "#169499",
            }).then(() => {
              history.push(
                `/servico-acolhimento/visualizar-regularidade/${id}`,
              );
            });
          } else {
            setLoading(false);
          }
        }
      });
  }, [history, id]);

  const schema = Yup.object().shape({
    numeroRegistro: Yup.string().when(["anexoNrRegistro"], {
      is: (anexoNrRegistro) => anexoNrRegistro,
      then: Yup.string().required("Este campo é obrigatório"),
      otherwise: Yup.string().nullable(),
    }),
    validadeNrRegistro: Yup.string()
      .nullable()
      .test("validadeBeforeToday", "Validade expirada!", (value) => {
        if (!Boolean(value) || data.naturezaServico === "publica") {
          return true;
        } else {
          const inputDate = parse(value, "dd/MM/yyyy", new Date());
          const currentDate = startOfDay(new Date());

          return (
            !isBefore(inputDate, currentDate) || isEqual(inputDate, currentDate)
          );
        }
      })
      .test("validDate", "Data inválida!", (value) => {
        if (!!value && data.naturezaServico === "privada") {
          if (value.length === 10) {
            return isValid(parse(value, "dd/MM/yyyy", new Date()));
          } else {
            return false;
          }
        } else {
          return true;
        }
      }),
    anexoNrRegistro: Yup.mixed()
      .test("fileSize", "O arquivo tem mais de 10MB.", (value) => {
        if (value === null) {
          return true;
        } else {
          return value.size <= 10000000;
        }
      })
      .test("fileType", "Anexar uma extensão válida.", (value) => {
        if (value === null) {
          return true;
        } else {
          let haveFormat = false;
          suportedFormats.forEach((format) => {
            if (value.path.toString().toLowerCase().includes(format)) {
              haveFormat = true;
            }
          });
          return haveFormat;
        }
      }),
    dataAnexoNrRegistro: Yup.string().when("anexoNrRegistro", {
      is: (anexoNrRegistro) => anexoNrRegistro,
      then: Yup.string()
        .nullable()
        .required("Este campo é obrigatório")
        .test("validDate", "Data inválida!", (value) => {
          if (!value) return false;
          if (value.length !== 10) return false;
          return isValid(parse(value, "dd/MM/yyyy", new Date()));
        }),
      otherwise: Yup.string()
        .nullable()
        .test("validDate", "Data inválida!", (value) => {
          if (!value) return true;
          if (value.length !== 10) return false;
          return isValid(parse(value, "dd/MM/yyyy", new Date()));
        }),
    }),
    numeroInscricao: Yup.string().when("anexoNrInscricao", {
      is: (anexoNrInscricao) => anexoNrInscricao,
      then: Yup.string().required("Este campo é obrigatório").nullable(),
      otherwise: Yup.string().nullable(),
    }),
    validadeNrInscricao: Yup.string()
      .nullable()
      .test("validadeBeforeToday", "Validade expirada!", (value) => {
        if (!Boolean(value)) {
          return true;
        } else {
          const inputDate = parse(value, "dd/MM/yyyy", new Date());
          const currentDate = startOfDay(new Date());

          return (
            !isBefore(inputDate, currentDate) || isEqual(inputDate, currentDate)
          );
        }
      })
      .test("validDate", "Data inválida!", (value) => {
        if (!!value) {
          if (value.length === 10) {
            return isValid(parse(value, "dd/MM/yyyy", new Date()));
          } else {
            return false;
          }
        } else {
          return true;
        }
      }),
    anexoNrInscricao: Yup.mixed()
      .test("fileSize", "O arquivo tem mais de 10MB.", (value) => {
        if (value === null) {
          return true;
        } else {
          return value.size <= 10000000;
        }
      })
      .test("fileType", "Anexar uma extensão válida.", (value) => {
        if (value === null) {
          return true;
        } else {
          let haveFormat = false;
          suportedFormats.forEach((format) => {
            if (value.path.toString().toLowerCase().includes(format)) {
              haveFormat = true;
            }
          });
          return haveFormat;
        }
      }),
    dataAnexoNrInscricao: Yup.string().when("anexoNrInscricao", {
      is: (anexoNrInscricao) => anexoNrInscricao,
      then: Yup.string()
        .nullable()
        .required("Este campo é obrigatório")
        .test("validDate", "Data inválida!", (value) => {
          if (!value) return false;
          if (value.length !== 10) return false;
          return isValid(parse(value, "dd/MM/yyyy", new Date()));
        }),
      otherwise: Yup.string()
        .nullable()
        .test("validDate", "Data inválida!", (value) => {
          if (!value) return true;
          if (value.length !== 10) return false;
          return isValid(parse(value, "dd/MM/yyyy", new Date()));
        }),
    }),

    numeroInscricaoCMAS: Yup.string().when(["anexoNrInscricaoCMAS"], {
      is: (anexoNrInscricaoCMAS) =>
        data.naturezaServico === "privada" && anexoNrInscricaoCMAS,
      then: Yup.string().required("Este campo é obrigatório").nullable(),
      otherwise: Yup.string().nullable(),
    }),

    validadeNrInscricaoCMAS: Yup.string()
      .nullable()
      .test("validadeBeforeToday", "Validade expirada!", (value) => {
        if (!Boolean(value) || data.naturezaServico === "publica") {
          return true;
        } else {
          const inputDate = parse(value, "dd/MM/yyyy", new Date());
          const currentDate = startOfDay(new Date());

          return (
            !isBefore(inputDate, currentDate) || isEqual(inputDate, currentDate)
          );
        }
      })
      .test("validDate", "Data inválida!", (value) => {
        if (!!value && data.naturezaServico === "privada") {
          if (value.length === 10) {
            return isValid(parse(value, "dd/MM/yyyy", new Date()));
          } else {
            return false;
          }
        } else {
          return true;
        }
      }),
    anexoNrInscricaoCMAS: Yup.mixed()
      .test("fileSize", "O arquivo tem mais de 10MB.", (value) => {
        if (value === null) {
          return true;
        } else {
          return value.size <= 10000000;
        }
      })
      .test("fileType", "Anexar uma extensão válida.", (value) => {
        if (value === null) {
          return true;
        } else {
          let haveFormat = false;
          suportedFormats.forEach((format) => {
            if (value.path.toString().toLowerCase().includes(format)) {
              haveFormat = true;
            }
          });
          return haveFormat;
        }
      }),
    dataAnexoNrInscricaoCMAS: Yup.string().when("anexoNrInscricaoCMAS", {
      is: (anexoNrInscricaoCMAS) => anexoNrInscricaoCMAS,
      then: Yup.string()
        .nullable()
        .required("Este campo é obrigatório")
        .test("validDate", "Data inválida!", (value) => {
          if (!value) return false;
          if (value.length !== 10) return false;
          return isValid(parse(value, "dd/MM/yyyy", new Date()));
        }),
      otherwise: Yup.string()
        .nullable()
        .test("validDate", "Data inválida!", (value) => {
          if (!value) return true;
          if (value.length !== 10) return false;
          return isValid(parse(value, "dd/MM/yyyy", new Date()));
        }),
    }),
  });

  const handleSubmit = (values, actions) => {
    setTimeout(() => {
      const {
        numeroRegistro,
        numeroInscricao,
        numeroInscricaoCMAS,
        validadeNrRegistro,
        validadeNrInscricao,
        validadeNrInscricaoCMAS,
        dataAnexoNrRegistro,
        dataAnexoNrInscricao,
        dataAnexoNrInscricaoCMAS,
        anexoNrInscricaoCMAS,
        anexoNrInscricao,
        ...resto
      } = values;

      const valuesToSend =
        data.naturezaServico === "publica"
          ? {
              numeroRegistro: numeroRegistro ? numeroRegistro.trim() : "",
              numeroInscricao: numeroInscricao ? numeroInscricao.trim() : "",
              numeroInscricaoCMAS: numeroInscricaoCMAS
                ? numeroInscricaoCMAS.trim()
                : "",
              validadeNrRegistro: validadeNrRegistro
                ? parse(
                    validadeNrRegistro,
                    "dd/MM/yyyy",
                    new Date(),
                  ).toISOString()
                : "",
              validadeNrInscricao: validadeNrInscricao
                ? parse(
                    validadeNrInscricao,
                    "dd/MM/yyyy",
                    new Date(),
                  ).toISOString()
                : "",
              validadeNrInscricaoCMAS: validadeNrInscricaoCMAS
                ? parse(
                    validadeNrInscricaoCMAS,
                    "dd/MM/yyyy",
                    new Date(),
                  ).toISOString()
                : "",
              dataAnexoNrRegistro: dataAnexoNrRegistro
                ? parse(
                    dataAnexoNrRegistro,
                    "dd/MM/yyyy",
                    new Date(),
                  ).toISOString()
                : "",
              dataAnexoNrInscricao: dataAnexoNrInscricao
                ? parse(
                    dataAnexoNrInscricao,
                    "dd/MM/yyyy",
                    new Date(),
                  ).toISOString()
                : "",
              dataAnexoNrInscricaoCMAS: dataAnexoNrInscricaoCMAS
                ? parse(
                    dataAnexoNrInscricaoCMAS,
                    "dd/MM/yyyy",
                    new Date(),
                  ).toISOString()
                : "",
              anexoNrInscricao: anexoNrInscricao ? anexoNrInscricao : null,
              ...resto,
            }
          : {
              numeroRegistro: numeroRegistro ? numeroRegistro.trim() : "",
              numeroInscricao: numeroInscricao ? numeroInscricao.trim() : "",
              numeroInscricaoCMAS: numeroInscricaoCMAS
                ? numeroInscricaoCMAS.trim()
                : "",
              validadeNrRegistro: validadeNrRegistro
                ? parse(
                    validadeNrRegistro,
                    "dd/MM/yyyy",
                    new Date(),
                  ).toISOString()
                : "",
              validadeNrInscricao: validadeNrInscricao
                ? parse(
                    validadeNrInscricao,
                    "dd/MM/yyyy",
                    new Date(),
                  ).toISOString()
                : "",
              validadeNrInscricaoCMAS: validadeNrInscricaoCMAS
                ? parse(
                    validadeNrInscricaoCMAS,
                    "dd/MM/yyyy",
                    new Date(),
                  ).toISOString()
                : "",
              dataAnexoNrRegistro: dataAnexoNrRegistro
                ? parse(
                    dataAnexoNrRegistro,
                    "dd/MM/yyyy",
                    new Date(),
                  ).toISOString()
                : "",
              dataAnexoNrInscricao: dataAnexoNrInscricao
                ? parse(
                    dataAnexoNrInscricao,
                    "dd/MM/yyyy",
                    new Date(),
                  ).toISOString()
                : "",
              dataAnexoNrInscricaoCMAS: dataAnexoNrInscricaoCMAS
                ? parse(
                    dataAnexoNrInscricaoCMAS,
                    "dd/MM/yyyy",
                    new Date(),
                  ).toISOString()
                : "",
              anexoNrInscricaoCMAS: anexoNrInscricaoCMAS
                ? anexoNrInscricaoCMAS
                : null,
              anexoNrInscricao: anexoNrInscricao ? anexoNrInscricao : null,
              ...resto,
            };

      ServicoAcolhimentoService.getServicoAcolhimento(id)
        .then((res) => {
          if (data.naturezaServico !== res.data.naturezaServico) {
            setData(res.data);
            Swal.fire({
              title: "Atenção!",
              text: "A natureza do serviço foi alterada, revise as informações para cadastro!",
              icon: "warning",
              confirmButtonText: "Fechar",
              confirmButtonColor: "#169499",
            }).then(() => {
              actions.setSubmitting(false);
            });
          } else {
            RegularidadeSAService.novaRegularidadeSA(id, valuesToSend)
              .then((res) => {
                Swal.fire({
                  title: "Sucesso!",
                  text: "Registro cadastrado com sucesso!",
                  icon: "success",
                  confirmButtonText: "Continuar",
                  confirmButtonColor: "#169499",
                }).then(function () {
                  history.push(
                    "/servico-acolhimento/visualizar-regularidade/" + id,
                  );
                  setCadastrarRegularidadeServ(false);
                });
                actions.setSubmitting(false);
              })
              .catch((error) => {
                if (error.response) {
                  if (error.response.data) {
                    if (error.response.data.message) {
                      if (error.response.data.field) {
                        if (error.response.data.field === "Regularidade") {
                          Swal.fire({
                            title: "Atenção!",
                            text: error.response.data.message
                              ? JSON.parse(error.response.data.message)
                                  .regularidade
                              : "",
                            icon: "warning",
                            confirmButtonText: "Fechar",
                            confirmButtonColor: "#169499",
                          }).then(() => {});
                        } else {
                          const errors = JSON.parse(
                            error.response.data.message,
                          );
                          for (let key in errors) {
                            actions.setFieldError(key.toString(), errors[key]);
                          }
                        }
                      } else {
                        Swal.fire({
                          title: "Atenção!",
                          text: error.response.data.message
                            ? JSON.parse(error.response.data.message)
                                .regularidade
                            : "",
                          icon: "warning",
                          confirmButtonText: "Fechar",
                          confirmButtonColor: "#169499",
                        }).then(() => {});
                      }
                    } else if (error.response.data.apierror) {
                      if (error.response.data.apierror.subErrors) {
                        error.response.data.apierror.subErrors.forEach(
                          (erro) => {
                            actions.setFieldError(erro.field, erro.message);
                          },
                        );
                      }
                    }
                  }
                } else {
                  console.log(error);
                }
                actions.setSubmitting(false);
              });
          }
        })
        .catch((err) => {
          console.log("Erro na consulta do SA no submit", err);
        });
    }, 200);
  };

  return roleKeycloack &&
    (roleKeycloack.includes(servicos.regularidadeCadastrar) ||
      (roleKeycloack?.includes(criancaFunc.visualizarTudoAcolhimento) &&
        user?.orgaoExterno?.includes(Number(data?.idPessoaJuridica)))) ? (
    !!data && !loading ? (
      <Formik
        validationSchema={schema}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {(props) => (
          <Form>
            {setCadastrarRegularidadeServ(
              hasFormChanged(initialValues, props.values),
            )}
            <Box
              mt={"15px"}
              border={
                ["59"].includes(idTipoPendencia) ? "3px solid #4299E199" : ""
              }
              ref={anexoRef}
            >
              <Flex
                flexDir="column"
                px="13px"
                py="25px"
                borderRadius="16px"
                w="100%"
                bgColor="#FFF"
              >
                <Text
                  color={"#169499"}
                  fontWeight="700"
                  fontSize="16px"
                  lineHeight="21.6px"
                  textTransform="uppercase"
                >
                  CMDCA
                </Text>
                <Divider orientation="horizontal" mt="10px" />
                {data.naturezaServico === "privada" && (
                  <>
                    <Grid
                      templateColumns={{
                        base: "repeat(1, 1fr)",
                        lg: "repeat(4, 1fr)",
                      }}
                      gap="15px"
                    >
                      <GridItem colSpan={2}>
                        <Field name="numeroRegistro">
                          {({ field, form }) => (
                            <FormControl
                              isInvalid={
                                form.errors.numeroRegistro &&
                                form.touched.numeroRegistro
                              }
                              id="numeroRegistro"
                              mt="10px"
                            >
                              <FormLabel
                                color="#5F6377"
                                fontWeight="500"
                                fontSize="14px"
                                lineHeight="19.6px"
                                htmlFor="numeroRegistro"
                              >
                                Número de Registro
                              </FormLabel>
                              <Input
                                {...field}
                                h="42px"
                                name="numeroRegistro"
                                id="numeroRegistro"
                                backgroundColor="#F9FAFB"
                                autoComplete="off"
                                border="1px solid #D7D8DE"
                                placeholder="Informe o Número de Registro"
                                onChange={(e) => {
                                  form.setFieldValue(
                                    "numeroRegistro",
                                    e.target.value,
                                  );
                                }}
                              />
                              <FormErrorMessage>
                                {form.errors.numeroRegistro}
                              </FormErrorMessage>
                            </FormControl>
                          )}
                        </Field>
                      </GridItem>
                      <GridItem colSpan={1}>
                        <CustomDatepickerWField
                          widthInput={"255px"}
                          fieldName="validadeNrRegistro"
                          label="Validade"
                          placeholder="Informe a Validade do Registro"
                          mt="10px !important"
                        />
                      </GridItem>
                      <GridItem colSpan={1} />
                      <GridItem colSpan={1}>
                        <Flex>
                          <CustomDropzone
                            formProps={props}
                            title="Anexar Registro no CMDCA"
                            fieldName="anexoNrRegistro"
                            height="42px"
                          />
                        </Flex>
                      </GridItem>
                      <GridItem colSpan={1} />
                      <GridItem colSpan={1}>
                        <CustomDatepickerWField
                          widthInput={"255px"}
                          fieldName="dataAnexoNrRegistro"
                          label={`Data do Documento${
                            props.values.anexoNrRegistro ? "*" : ""
                          }`}
                          placeholder="Informe a data"
                        />
                      </GridItem>
                    </Grid>
                  </>
                )}
                <Grid
                  templateColumns={{
                    base: "repeat(1, 1fr)",
                    lg: "repeat(4, 1fr)",
                  }}
                  gap="15px"
                  mt="20px"
                >
                  <GridItem colSpan={2}>
                    <Field name="numeroInscricao">
                      {({ field, form }) => (
                        <FormControl
                          isInvalid={
                            form.errors.numeroInscricao &&
                            form.touched.numeroInscricao
                          }
                          id="numeroInscricao"
                          mt="10px"
                        >
                          <FormLabel
                            color="#5F6377"
                            fontWeight="500"
                            fontSize="14px"
                            lineHeight="19.6px"
                            htmlFor="numeroInscricao"
                          >
                            Número de Inscrição
                            {props.values.anexoNrInscricao ? "*" : ""}
                          </FormLabel>
                          <Input
                            {...field}
                            h="42px"
                            name="numeroInscricao"
                            id="numeroInscricao"
                            backgroundColor="#F9FAFB"
                            autoComplete="off"
                            border="1px solid #D7D8DE"
                            placeholder="Informe o Número de Inscrição"
                            onChange={(e) => {
                              form.setFieldValue(
                                "numeroInscricao",
                                e.target.value,
                              );
                            }}
                          />
                          <FormErrorMessage>
                            {form.errors.numeroInscricao}
                          </FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                  </GridItem>
                  <GridItem colSpan={1}>
                    <CustomDatepickerWField
                      widthInput={"255px"}
                      fieldName="validadeNrInscricao"
                      label="Validade"
                      placeholder="Informe a Validade da Inscrição"
                      mt="10px !important"
                    />
                  </GridItem>
                  <GridItem colSpan={1} />
                  <GridItem colSpan={1}>
                    <Flex>
                      <CustomDropzone
                        formProps={props}
                        title={`Anexar Inscrição no CMDCA`}
                        fieldName="anexoNrInscricao"
                        height="42px"
                      />
                    </Flex>
                  </GridItem>
                  <GridItem colSpan={1} />
                  <GridItem colSpan={1}>
                    <CustomDatepickerWField
                      widthInput={"255px"}
                      fieldName="dataAnexoNrInscricao"
                      label={`Data do Documento${
                        props.values.anexoNrInscricao ? "*" : ""
                      }`}
                      placeholder="Informe a data"
                    />
                  </GridItem>
                </Grid>
              </Flex>
              {data.naturezaServico === "privada" && (
                <>
                  <Flex
                    mt="15px"
                    flexDir="column"
                    px="13px"
                    pb="25px"
                    borderRadius="16px"
                    w="100%"
                    bgColor="#FFF"
                  >
                    <Text
                      color={"#169499"}
                      fontWeight="700"
                      fontSize="16px"
                      lineHeight="21.6px"
                      textTransform="uppercase"
                      mt="20px"
                    >
                      CMAS
                    </Text>
                    <Divider orientation="horizontal" my="10px" />
                    <Grid
                      templateColumns={{
                        base: "repeat(1, 1fr)",
                        lg: "repeat(4, 1fr)",
                      }}
                      gap="15px"
                    >
                      <GridItem colSpan={2}>
                        <Field name="numeroInscricaoCMAS">
                          {({ field, form }) => (
                            <FormControl
                              isInvalid={
                                form.errors.numeroInscricaoCMAS &&
                                form.touched.numeroInscricaoCMAS
                              }
                              id="numeroInscricaoCMAS"
                              mt="10px"
                            >
                              <FormLabel
                                color="#5F6377"
                                fontWeight="500"
                                fontSize="14px"
                                lineHeight="19.6px"
                                htmlFor="numeroInscricaoCMAS"
                              >
                                Número de Inscrição
                                {props.values.anexoNrInscricaoCMAS ? "*" : ""}
                              </FormLabel>
                              <Input
                                {...field}
                                h="42px"
                                name="numeroInscricaoCMAS"
                                id="numeroInscricaoCMAS"
                                backgroundColor="#F9FAFB"
                                autoComplete="off"
                                border="1px solid #D7D8DE"
                                placeholder="Informe o Número de Inscrição"
                                onChange={(e) => {
                                  form.setFieldValue(
                                    "numeroInscricaoCMAS",
                                    e.target.value,
                                  );
                                }}
                              />
                              <FormErrorMessage>
                                {form.errors.numeroInscricaoCMAS}
                              </FormErrorMessage>
                            </FormControl>
                          )}
                        </Field>
                      </GridItem>
                      <GridItem colSpan={1}>
                        <CustomDatepickerWField
                          widthInput={"255px"}
                          fieldName="validadeNrInscricaoCMAS"
                          label="Validade"
                          placeholder="Informe a Validade da Inscrição"
                          mt="10px !important"
                        />
                      </GridItem>
                      <GridItem colSpan={1} />
                      <GridItem colSpan={1}>
                        <Flex>
                          <CustomDropzone
                            formProps={props}
                            title="Anexar Inscrição no CMAS"
                            fieldName="anexoNrInscricaoCMAS"
                            height="42px"
                          />
                        </Flex>
                      </GridItem>
                      <GridItem colSpan={1} />
                      <GridItem colSpan={1}>
                        <CustomDatepickerWField
                          widthInput={"255px"}
                          fieldName="dataAnexoNrInscricaoCMAS"
                          label={`Data do Documento${
                            props.values.anexoNrInscricaoCMAS ? "*" : ""
                          }`}
                          placeholder="Informe a data"
                        />
                      </GridItem>
                    </Grid>
                  </Flex>
                </>
              )}
            </Box>
            <Flex mt="30px" justifyContent="flex-end">
              <Button
                id="button-salvar"
                type="submit"
                translate="no"
                color="#FFF"
                backgroundColor="#169499"
                fontWeight={700}
                fontSize="15px"
                lineHeight="21.6px"
                _hover={{ bg: "#169499" }}
                w="17%"
                isLoading={props.isSubmitting}
              >
                Salvar
              </Button>
            </Flex>
          </Form>
        )}
      </Formik>
    ) : (
      <LoadingSpinner loading={data === undefined} />
    )
  ) : (
    <Box mt={2}>
      <SemRegistros buttonVisible={true} half />
    </Box>
  );
};

export default CadastrarRegularidade;
